import { logger } from 'src/lib/logger'
import { PropertyTypes } from 'src/lib/objects'
import { slugify } from 'src/lib/utils'

import { initialOpportunityState } from './types'
import type {
  OpportunityState,
  OpportunityAction,
  PropertyDefinitionState,
} from './types'

export const opportunityReducer = (
  state: OpportunityState,
  action: OpportunityAction
): OpportunityState => {
  switch (action.type) {
    case 'SET_TITLE':
      return { ...state, title: action.payload }
    case 'SET_TYPE':
      return { ...state, oppType: action.payload }
    case 'SET_OPP_TYPE':
      return { ...state, oppType: action.payload }
    case 'SET_OWNER':
      return {
        ...state,
        selectedOwner: action.payload,
        ownerWasDismissed: false,
      }
    case 'DISMISS_OWNER':
      return { ...state, selectedOwner: null, ownerWasDismissed: true }
    case 'SET_PIPELINE':
      return {
        ...state,
        selectedPipeline: action.payload,
        hasRevenue: action.payload?.hasRevenue ?? false,
      }
    case 'SET_STAGE':
      return { ...state, selectedStage: action.payload }
    case 'SET_ORGANIZATION':
      return {
        ...state,
        selectedOrganization: action.payload,
        domain: action.payload?.domain ?? null,
        title:
          state.title || action.payload?.name || action.payload?.domain || '',
      }
    case 'SET_PERSON':
      return { ...state, selectedPerson: action.payload }
    case 'SET_EXPECTED_CLOSE_DATE':
      return { ...state, expectedCloseDate: action.payload }
    case 'SET_EXPECTED_REVENUE':
      return { ...state, expectedRevenue: action.payload }
    case 'SET_HAS_REVENUE':
      return { ...state, hasRevenue: action.payload }
    case 'SET_DOMAIN':
      return { ...state, domain: action.payload }
    case 'RESET':
      return initialOpportunityState
    case 'INITIALIZE':
      return { ...state, ...action.payload }
    default:
      return state
  }
}

// Types
export type PropertyDefinitionAction =
  | { type: 'SET_NAME'; payload: string }
  | { type: 'SET_DESCRIPTION'; payload: string }
  | { type: 'SET_PROPERTY_TYPE'; payload: string }
  | { type: 'SET_AI_ENABLED'; payload: boolean }
  | { type: 'SET_USE_WEB'; payload: boolean }
  | {
      type: 'SET_OPTIONS'
      payload: Array<{ id: string; name: string; description?: string }>
    }
  | { type: 'ADD_OPTION'; payload: { name: string; description: string } }
  | { type: 'REMOVE_OPTION'; payload: string }
  | {
      type: 'EDIT_OPTION'
      payload: { id: string; name: string; description: string }
    }

export interface PropertyDefinitionFormProps {
  workspaceId: string
  state: {
    name: string
    description: string
    propertyType: string
    aiManaged: boolean
    useWeb: boolean
    options: Array<{
      id: string
      name: string
      description?: string
    }>
    selectedDefinition: any | null
  }
  dispatch: React.Dispatch<PropertyDefinitionAction>
  onSubmit: () => Promise<void>
  onDelete?: () => Promise<void>
}

export function isPicklist(propertyType: string) {
  return (
    propertyType === PropertyTypes.MultiPicklist ||
    propertyType === PropertyTypes.Picklist ||
    propertyType === PropertyTypes.Combobox
  )
}

export function anyAiEnabled(state: PropertyDefinitionFormProps['state']) {
  return state.aiManaged || state.useWeb
}

export function propertyDefinitionReducer(
  state: PropertyDefinitionState,
  action: PropertyDefinitionAction
): PropertyDefinitionState {
  logger.dev({ action })
  switch (action.type) {
    case 'SET_NAME':
      return { ...state, name: action.payload }
    case 'SET_DESCRIPTION':
      return { ...state, description: action.payload }
    case 'SET_PROPERTY_TYPE':
      return { ...state, propertyType: action.payload }
    case 'SET_AI_ENABLED':
      return { ...state, aiManaged: action.payload }
    case 'SET_USE_WEB':
      return { ...state, useWeb: action.payload }
    case 'SET_OPTIONS':
      return { ...state, options: action.payload }
    case 'ADD_OPTION':
      return {
        ...state,
        options: [
          ...state.options,
          {
            id: slugify(action.payload.name),
            name: action.payload.name,
            description: action.payload.description,
          },
        ],
      }
    case 'REMOVE_OPTION':
      return {
        ...state,
        options: state.options.filter((option) => option.id !== action.payload),
      }
    case 'EDIT_OPTION':
      return {
        ...state,
        options: state.options.map((option) =>
          option.id === action.payload.id
            ? {
                id: option.id,
                name: action.payload.name,
                description: action.payload.description,
              }
            : option
        ),
      }
    default:
      return state
  }
}
